import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  Grid,
  Card,
  CardContent,
  Divider,
  TablePagination,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { collection, getDocs, deleteDoc, doc, query, where, writeBatch, getDoc } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import { validateDatabase } from "../../../../Utils/databaseGuard";
import Swal from "sweetalert2";
import dayjs from "dayjs";

const PerformanceLedgerDeduplicationTool = () => {
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState(new Set());
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
  const [userFilter, setUserFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [userDataMap, setUserDataMap] = useState(new Map());
  const [stats, setStats] = useState({
    totalPerformanceLedgers: 0,
    duplicateGroups: 0,
    duplicateEntries: 0,
    affectedUsers: 0,
    individualDuplicates: 0,
    corporateDuplicates: 0,
    totalProfitLossIndividual: 0,
    totalProfitLossCorporate: 0,
    totalProfitLoss: 0,
  });

  useEffect(() => {
    // Auto-load on mount
    handleAnalyze();
  }, []);

  const loadUserData = async (userIds) => {
    const userMap = new Map();
    try {
      const usersSnapshot = await getDocs(collection(DATABASE, "users"));
      usersSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        userMap.set(doc.id, {
          id: doc.id,
          name: data.full_name || data.first_name || "Unknown User",
          email: data.email || "Unknown Email",
          user_type: data.user_type !== undefined ? data.user_type : 0, // 0 = individual, 1 = corporate
        });
      });
    } catch (error) {
      console.error("Error loading user data:", error);
    }
    return userMap;
  };

  const handleAnalyze = async () => {
    validateDatabase();
    setLoading(true);
    setAnalysisComplete(false);
    setDuplicates([]);
    setSelectedDuplicates(new Set());

    try {
      console.log("🔍 Starting duplicate analysis...");

      // Get ALL ledgers
      const ledgersQuery = query(collection(DATABASE, "ledgers"));
      const ledgersSnapshot = await getDocs(ledgersQuery);
      const allLedgers = ledgersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log(`📊 Total ledgers: ${allLedgers.length}`);

      // Filter for performance ledgers (transection_type === 3)
      const performanceLedgers = allLedgers.filter(
        (ledger) => ledger.transection_type === 3
      );

      console.log(`📊 Performance ledgers: ${performanceLedgers.length}`);

      // Get unique user IDs
      const userIds = new Set(performanceLedgers.map((l) => l.user_id).filter(Boolean));
      console.log(`👥 Loading user data for ${userIds.size} users...`);
      const userMap = await loadUserData(Array.from(userIds));
      setUserDataMap(userMap);

      // SIMPLE AND DIRECT APPROACH: Group by user + month/year, then find duplicates within each group
      // This ensures we find ALL duplicates without missing any
      const userMonthGroups = new Map(); // Key: userId-year-month, Value: array of entries

      performanceLedgers.forEach((ledger) => {
        const userId = ledger.user_id;
        if (!userId) return;

        // Use the 'date' field from ledger - this is the actual transaction date
        let date = null;
        if (ledger.date) {
          date = ledger.date?.toDate ? ledger.date.toDate() : new Date(ledger.date);
        } else if (ledger.created_at) {
          // Fallback to created_at if date doesn't exist
          date = ledger.created_at?.toDate ? ledger.created_at.toDate() : new Date(ledger.created_at);
        } else {
          // Skip if no date available
          console.warn(`⚠️ Ledger ${ledger.id} has no date field`);
          return;
        }

        // Validate date
        if (isNaN(date.getTime())) {
          console.warn(`⚠️ Ledger ${ledger.id} has invalid date: ${ledger.date}`);
          return;
        }

        let month = date.getMonth() + 1; // getMonth() returns 0-11, so add 1
        let year = date.getFullYear();
        const day = date.getDate();
        const hour = date.getHours();
        
        // CRITICAL FIX: If entry is migrated and has date at end of month (23:00), 
        // it's actually for the NEXT month's performance
        // Example: 7/31/2025 23:00 is actually August 2025 performance
        if (ledger._migrated_from && ledger._migrated_from !== "No") {
          // Check if date is last day of month and time is 23:00 (11 PM)
          const lastDayOfMonth = new Date(year, month, 0).getDate(); // Last day of current month
          if (day === lastDayOfMonth && hour >= 23) {
            // This is actually for the NEXT month
            month = month + 1;
            if (month > 12) {
              month = 1;
              year = year + 1;
            }
          }
        }
        
        const performance = parseFloat(ledger.profit_loss_percent || 0);
        const profitLossBalance = parseFloat(ledger.profit_loss_balance || 0);
        const amount = parseFloat(ledger.amount || 0);
        const created_at = ledger.created_at?.toDate?.() || ledger._migrated_at?.toDate?.() || date;

        const entry = {
          id: ledger.id,
          doc_id: ledger.doc_id || "N/A",
          userId,
          date: date.toLocaleString(),
          dateObj: date, // Store original date object for sorting
          month,
          year,
          day,
          performance,
          profitLossBalance,
          amount,
          balance: ledger.balance || ledger.current_balance || 0,
          _migrated_from: ledger._migrated_from || "No",
          _migrated_at: ledger._migrated_at || null,
          created_at: created_at,
          collection: "ledgers",
        };

        // Group by user + month/year - this ensures entries from different months are NOT grouped together
        // Now using the CORRECT month (adjusted for migrated entries at end of month)
        const groupKey = `${userId}-${year}-${String(month).padStart(2, "0")}`;
        if (!userMonthGroups.has(groupKey)) {
          userMonthGroups.set(groupKey, []);
        }
        userMonthGroups.get(groupKey).push(entry);
      });

      // Group entries by same user + month + SAME profit_loss_balance
      // If profit_loss_balance is the same, it's the same transaction (duplicate)
      // Performance % might differ slightly due to calculation differences, but same $ amount = duplicate
      const duplicateGroupsMap = new Map();
      let groupIndex = 0;
      
      userMonthGroups.forEach((entries, groupKey) => {
        // If more than 1 entry for same user+month, group by profit_loss_balance
        if (entries.length > 1) {
          // Group by profit_loss_balance (rounded to 2 decimals to avoid floating point issues)
          const valueGroups = new Map();
          
          entries.forEach(entry => {
            // Round to 2 decimals for money comparison
            const profitKey = Math.round(entry.profitLossBalance * 100) / 100;
            const valueKey = profitKey.toFixed(2);
            
            if (!valueGroups.has(valueKey)) {
              valueGroups.set(valueKey, []);
            }
            valueGroups.get(valueKey).push(entry);
          });
          
          // Create duplicate groups for entries with SAME profit_loss_balance
          valueGroups.forEach((sameValueEntries, valueKey) => {
            if (sameValueEntries.length > 1) {
              // Verify all entries are from the same month/year (safety check)
              const firstEntry = sameValueEntries[0];
              const allSameMonth = sameValueEntries.every(e => 
                e.year === firstEntry.year && e.month === firstEntry.month
              );
              
              if (!allSameMonth) {
                console.error(`❌ ERROR: Group ${groupKey} has entries from different months!`, sameValueEntries);
                // Still add it, but log the error
              }
              
              duplicateGroupsMap.set(`group-${groupIndex++}`, sameValueEntries);
            }
          });
        }
      });
      
      console.log(`📊 Found ${duplicateGroupsMap.size} duplicate groups`);

      // Process all duplicate groups found
      const duplicateGroups = [];
      duplicateGroupsMap.forEach((entries, key) => {
        // CRITICAL: Sort to PREFER keeping MIGRATED entries (from production)
        // These are the correct ones that clients need to see
        entries.sort((a, b) => {
          // Priority 1: Prefer MIGRATED entries (from production) - these are CORRECT
          if (a._migrated_from !== "No" && b._migrated_from === "No") return -1;
          if (a._migrated_from === "No" && b._migrated_from !== "No") return 1;
          
          // Priority 2: If both migrated or both not migrated, prefer older (original production date)
          // Use dateObj if available, otherwise parse the date string
          const dateA = a.dateObj || new Date(a.date);
          const dateB = b.dateObj || new Date(b.date);
          return dateA.getTime() - dateB.getTime(); // Older first (original production)
        });

        // First entry is the one to KEEP (migrated from production)
        // All others are marked for deletion (created in Rowy)
        duplicateGroups.push({
          key,
          entries,
          count: entries.length,
          // Keep first entry (MIGRATED from production - CORRECT)
          keep: entries[0],
          delete: entries.slice(1), // These are created in Rowy - can be deleted
        });
      });

      console.log(`🔍 Found ${duplicateGroups.length} duplicate groups (after consolidation)`);
      console.log(`📊 Total duplicate entries: ${duplicateGroups.reduce((sum, g) => sum + g.delete.length, 0)}`);

      // Calculate stats
      const totalDuplicates = duplicateGroups.reduce(
        (sum, group) => sum + group.delete.length,
        0
      );
      const affectedUsers = new Set(
        duplicateGroups.map((group) => group.entries[0].userId)
      ).size;

      // Calculate individual vs corporate stats
      let individualDuplicates = 0;
      let corporateDuplicates = 0;
      let totalProfitLossIndividual = 0;
      let totalProfitLossCorporate = 0;

      duplicateGroups.forEach((group) => {
        const userId = group.entries[0].userId;
        const userData = userMap.get(userId);
        const userType = userData?.user_type !== undefined ? userData.user_type : 0;
        const profitLoss = group.entries[0].profitLossBalance;
        const duplicatesCount = group.delete.length;

        if (userType === 0) {
          // Individual
          individualDuplicates += duplicatesCount;
          totalProfitLossIndividual += profitLoss * duplicatesCount;
        } else {
          // Corporate
          corporateDuplicates += duplicatesCount;
          totalProfitLossCorporate += profitLoss * duplicatesCount;
        }
      });

      setStats({
        totalPerformanceLedgers: performanceLedgers.length,
        duplicateGroups: duplicateGroups.length,
        duplicateEntries: totalDuplicates,
        affectedUsers,
        individualDuplicates,
        corporateDuplicates,
        totalProfitLossIndividual,
        totalProfitLossCorporate,
        totalProfitLoss: totalProfitLossIndividual + totalProfitLossCorporate,
      });

      setDuplicates(duplicateGroups);
      setAnalysisComplete(true);

      console.log("✅ Analysis complete");
    } catch (error) {
      console.error("❌ Error analyzing duplicates:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to analyze duplicates: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = new Set();
      duplicates.forEach((group) => {
        group.delete.forEach((entry) => {
          allIds.add(entry.id);
        });
      });
      setSelectedDuplicates(allIds);
    } else {
      setSelectedDuplicates(new Set());
    }
  };

  const handleSelectDuplicate = (entryId, event) => {
    // Prevent event propagation to avoid conflicts
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    setSelectedDuplicates((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(entryId)) {
        newSelected.delete(entryId);
      } else {
        newSelected.add(entryId);
      }
      return newSelected;
    });
  };

  const handleSimulate = () => {
    setSimulateDialogOpen(true);
  };

  const handlePreview = () => {
    if (selectedDuplicates.size === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Items Selected",
        text: "Please select duplicates to preview",
      });
      return;
    }
    setPreviewDialogOpen(true);
  };

  const handleDelete = () => {
    if (selectedDuplicates.size === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Items Selected",
        text: "Please select duplicates to delete",
      });
      return;
    }
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);

      // Get user info for each duplicate to show impact
      const duplicatesToDelete = [];
      duplicates.forEach((group) => {
        group.delete.forEach((entry) => {
          if (selectedDuplicates.has(entry.id)) {
            duplicatesToDelete.push({
              ...entry,
              groupKey: group.key,
              keepEntry: group.keep,
            });
          }
        });
      });

      // Group by user to show impact
      const userImpact = new Map();
      duplicatesToDelete.forEach((entry) => {
        if (!userImpact.has(entry.userId)) {
          userImpact.set(entry.userId, []);
        }
        userImpact.get(entry.userId).push(entry);
      });

      // Delete in batches
      const batch = writeBatch(DATABASE);
      let deleted = 0;

      duplicatesToDelete.forEach((entry) => {
        const docRef = doc(DATABASE, "ledgers", entry.id);
        batch.delete(docRef);
        deleted++;
      });

      await batch.commit();

      // Show success with impact summary
      let impactMessage = `Successfully deleted ${deleted} duplicate ledgers.\n\n`;
      impactMessage += `Affected users: ${userImpact.size}\n`;
      impactMessage += `⚠️ Note: This does NOT affect user balances (balances are stored in users collection).\n`;
      impactMessage += `✅ Performance display will now show without duplicates.`;

      Swal.fire({
        icon: "success",
        title: "Success",
        html: impactMessage.replace(/\n/g, "<br>"),
        width: 600,
      });

      setSelectedDuplicates(new Set());
      setDeleteDialogOpen(false);
      handleAnalyze(); // Refresh
    } catch (error) {
      console.error("❌ Error deleting duplicates:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to delete duplicates: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = () => {
    // Select all duplicates that should be deleted (all entries marked for deletion)
    const allIds = new Set();
    duplicates.forEach((group) => {
      group.delete.forEach((entry) => {
        allIds.add(entry.id);
      });
    });
    setSelectedDuplicates(allIds);
    setSimulateDialogOpen(false);
    setDeleteDialogOpen(true);
  };

  const filteredDuplicates = duplicates.filter((group) => {
    if (!userFilter) return true;
    const userId = group.entries[0].userId;
    const userData = userDataMap.get(userId);
    const userName = userData?.name || "";
    const userEmail = userData?.email || "";
    const searchTerm = userFilter.toLowerCase();
    return (
      userId.toLowerCase().includes(searchTerm) ||
      userName.toLowerCase().includes(searchTerm) ||
      userEmail.toLowerCase().includes(searchTerm)
    );
  });

  const paginatedDuplicates = filteredDuplicates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getUserName = (userId) => {
    const userData = userDataMap.get(userId);
    return userData?.name || userId;
  };

  const getUserEmail = (userId) => {
    const userData = userDataMap.get(userId);
    return userData?.email || "Unknown Email";
  };

  const getUserType = (userId) => {
    const userData = userDataMap.get(userId);
    return userData?.user_type !== undefined ? userData.user_type : 0;
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" sx={{ color: "#fff" }}>
          Performance Ledger Deduplication Tool
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleAnalyze}
          disabled={loading}
          sx={{
            background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
            "&:hover": {
              background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
              opacity: 0.9,
            },
          }}
        >
          {loading ? <CircularProgress size={24} /> : "Analyze Duplicates"}
        </Button>
      </Box>

      <Alert severity="warning" sx={{ mb: 3, backgroundColor: "rgba(255, 152, 0, 0.1)", color: "#ff9800" }}>
        <Typography variant="body2">
          <strong>⚠️ CRITICAL - Read Carefully:</strong> This tool removes duplicate performance ledgers.
          <br />
          • <strong>✅ KEEPS:</strong> Migrated entries from production (<code>_migrated_from: fiddux-5a1f1</code>) - These are CORRECT and clients need to see them.
          <br />
          • <strong>🗑️ DELETES:</strong> Entries created in Rowy (without <code>_migrated_from</code>) - These are duplicates that can be safely removed.
          <br />
          • <strong>🛡️ Safety:</strong> User balances are stored in <code>users</code> collection and will NOT be affected. Monthly/Yearly graphs calculate from ledgers, so keeping the correct (migrated) ledger ensures graphs remain accurate.
          <br />
          • <strong>⚠️ Before Deleting:</strong> Verify that the entries marked for deletion are NOT being used by monthly_grpahs or yearly_graph calculations.
        </Typography>
      </Alert>

      {analysisComplete && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(42, 245, 152, 0.2)" }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: "#2af598" }}>{stats.totalPerformanceLedgers}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Total Performance Ledgers</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(255, 152, 0, 0.2)" }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: "#ff9800" }}>{stats.duplicateGroups}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Duplicate Groups</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(245, 42, 45, 0.2)" }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: "#f52a2d" }}>{stats.duplicateEntries}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Duplicate Entries</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(33, 150, 243, 0.2)" }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: "#2196f3" }}>{stats.affectedUsers}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Affected Users</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Duplicate Summary by User Type */}
          <Card sx={{ mb: 3, background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(42, 245, 152, 0.2)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: "#fff", mb: 2 }}>
                📊 Duplicate Summary by User Type
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, backgroundColor: "rgba(42, 245, 152, 0.1)", borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>Individual Users</Typography>
                    <Typography variant="h5" sx={{ color: "#2af598" }}>{stats.individualDuplicates}</Typography>
                    <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
                      Total Profit/Loss: ${stats.totalProfitLossIndividual.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, backgroundColor: "rgba(33, 150, 243, 0.1)", borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>Corporate Users</Typography>
                    <Typography variant="h5" sx={{ color: "#2196f3" }}>{stats.corporateDuplicates}</Typography>
                    <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
                      Total Profit/Loss: ${stats.totalProfitLossCorporate.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, backgroundColor: "rgba(255, 152, 0, 0.1)", borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>Total</Typography>
                    <Typography variant="h5" sx={{ color: "#ff9800" }}>{stats.duplicateEntries}</Typography>
                    <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
                      Total Profit/Loss: ${stats.totalProfitLoss.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Actions */}
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              label="Filter by User Name, Email, or ID"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              size="small"
              sx={{
                flex: 1,
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                },
                "& .MuiInputLabel-root": { color: "#aaa" },
              }}
            />
            <Button
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              onClick={handleSimulate}
              sx={{ color: "#fff", borderColor: "rgba(42, 245, 152, 0.3)" }}
            >
              Simulate
            </Button>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={handlePreview}
              disabled={selectedDuplicates.size === 0}
              sx={{ color: "#fff", borderColor: "rgba(42, 245, 152, 0.3)" }}
            >
              Preview ({selectedDuplicates.size})
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={selectedDuplicates.size === 0 || loading}
              sx={{
                background: "linear-gradient(148.97deg, #f52a2d 0%, #c2185b 100%)",
                "&:hover": {
                  background: "linear-gradient(148.97deg, #f52a2d 0%, #c2185b 100%)",
                  opacity: 0.9,
                },
              }}
            >
              Delete Selected ({selectedDuplicates.size})
            </Button>
          </Box>

          {/* Duplicates Table */}
          <TableContainer component={Paper} sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedDuplicates.size > 0 && selectedDuplicates.size === duplicates.reduce((sum, g) => sum + g.delete.length, 0)}
                      indeterminate={selectedDuplicates.size > 0 && selectedDuplicates.size < duplicates.reduce((sum, g) => sum + g.delete.length, 0)}
                      onChange={handleSelectAll}
                      sx={{ color: "#fff" }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#fff" }}>User Name</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Month/Year</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Performance %</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Profit Loss</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Count</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Created At</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Collection</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedDuplicates.map((group, index) => {
                  const userData = userDataMap.get(group.entries[0].userId);
                  const userType = getUserType(group.entries[0].userId);
                  return (
                    <React.Fragment key={group.key}>
                      <TableRow sx={{ backgroundColor: "rgba(255, 152, 0, 0.1)" }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={group.delete.length > 0 && group.delete.every((e) => selectedDuplicates.has(e.id))}
                            indeterminate={group.delete.some((e) => selectedDuplicates.has(e.id)) && !group.delete.every((e) => selectedDuplicates.has(e.id))}
                            onChange={(e) => {
                              e.stopPropagation(); // Prevent event bubbling
                              const newSelected = new Set(selectedDuplicates);
                              if (e.target.checked) {
                                // Select all entries in this group
                                group.delete.forEach((entry) => {
                                  newSelected.add(entry.id);
                                });
                              } else {
                                // Deselect all entries in this group
                                group.delete.forEach((entry) => {
                                  newSelected.delete(entry.id);
                                });
                              }
                              setSelectedDuplicates(newSelected);
                            }}
                            sx={{ color: "#fff" }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                              {getUserName(group.entries[0].userId)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#aaa" }}>
                              {getUserEmail(group.entries[0].userId)}
                            </Typography>
                            <Chip
                              label={userType === 0 ? "Individual" : "Corporate"}
                              size="small"
                              sx={{
                                ml: 1,
                                height: 18,
                                fontSize: "0.7rem",
                                backgroundColor: userType === 0 ? "rgba(42, 245, 152, 0.2)" : "rgba(33, 150, 243, 0.2)",
                                color: userType === 0 ? "#2af598" : "#2196f3",
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {String(group.entries[0].month).padStart(2, "0")}/{group.entries[0].year}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>{group.entries[0].performance.toFixed(4)}%</TableCell>
                        <TableCell sx={{ color: "#fff" }}>${group.entries[0].profitLossBalance.toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={`${group.count} entries`}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(255, 152, 0, 0.3)",
                              color: "#fff",
                              fontWeight: "bold",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {dayjs(group.keep.created_at).format("MM/DD/YYYY HH:mm")}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>{group.keep.collection}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip
                              label={group.keep._migrated_from !== "No" ? "✅ Migrated (KEEP)" : "⚠️ Rowy (KEEP)"}
                              size="small"
                              sx={{
                                backgroundColor: group.keep._migrated_from !== "No" ? "rgba(42, 245, 152, 0.3)" : "rgba(255, 152, 0, 0.3)",
                                color: "#fff",
                                fontWeight: "bold",
                              }}
                            />
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  Swal.fire({
                                    title: "Duplicate Group Details",
                                    html: `
                                      <div style="text-align: left;">
                                        <p><strong>User:</strong> ${getUserName(group.entries[0].userId)}</p>
                                        <p><strong>Email:</strong> ${getUserEmail(group.entries[0].userId)}</p>
                                        <p><strong>Month/Year:</strong> ${String(group.entries[0].month).padStart(2, "0")}/${group.entries[0].year}</p>
                                        <p><strong>Performance:</strong> ${group.entries[0].performance.toFixed(4)}%</p>
                                        <p><strong>Profit Loss:</strong> $${group.entries[0].profitLossBalance.toFixed(2)}</p>
                                        <hr />
                                        <p><strong>✅ KEEPING (Migrated from Production):</strong> ${group.keep.id}</p>
                                        <p style="color: #2af598;">   • Migrated: ${group.keep._migrated_from !== "No" ? "YES (fiddux-5a1f1)" : "NO"}</p>
                                        <p style="color: #2af598;">   • Date: ${group.keep.date}</p>
                                        <p style="color: #2af598;">   • Performance: ${group.keep.performance.toFixed(4)}% - $${group.keep.profitLossBalance.toFixed(2)}</p>
                                        <hr />
                                        <p><strong>🗑️ DELETING (Created in Rowy):</strong> ${group.delete.length} entries</p>
                                        ${group.delete.map((entry, idx) => `
                                          <p style="color: #f52a2d;">   ${idx + 1}. ${entry.id}</p>
                                          <p style="color: #f52a2d; margin-left: 20px;">   • Migrated: ${entry._migrated_from !== "No" ? "YES" : "NO (Created in Rowy)"}</p>
                                          <p style="color: #f52a2d; margin-left: 20px;">   • Date: ${entry.date}</p>
                                          <p style="color: #f52a2d; margin-left: 20px;">   • Performance: ${entry.performance.toFixed(4)}% - $${entry.profitLossBalance.toFixed(2)}</p>
                                        `).join("")}
                                      </div>
                                    `,
                                    width: 600,
                                  });
                                }}
                                sx={{ color: "#2196f3" }}
                              >
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      {group.delete.map((entry, deleteIndex) => {
                        const userData = userDataMap.get(entry.userId);
                        const userType = getUserType(entry.userId);
                        // Use unique key combining group key and entry id to avoid conflicts
                        const uniqueKey = `${group.key}-delete-${entry.id}-${deleteIndex}`;
                        return (
                          <TableRow key={uniqueKey} sx={{ backgroundColor: "rgba(245, 42, 45, 0.05)" }}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedDuplicates.has(entry.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleSelectDuplicate(entry.id, e);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                sx={{ color: "#fff" }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: "#aaa", pl: 4 }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                  → {getUserName(entry.userId)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#888" }}>
                                  {getUserEmail(entry.userId)}
                                </Typography>
                                <Chip
                                  label={userType === 0 ? "Individual" : "Corporate"}
                                  size="small"
                                  sx={{
                                    ml: 1,
                                    height: 18,
                                    fontSize: "0.7rem",
                                    backgroundColor: userType === 0 ? "rgba(42, 245, 152, 0.2)" : "rgba(33, 150, 243, 0.2)",
                                    color: userType === 0 ? "#2af598" : "#2196f3",
                                  }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: "#aaa" }}>{entry.date}</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>{entry.performance.toFixed(4)}%</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>${entry.profitLossBalance.toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={entry._migrated_from === "No" ? "Created in Rowy" : "Migrated"}
                            size="small"
                            sx={{
                              backgroundColor: entry._migrated_from === "No" ? "rgba(245, 42, 45, 0.3)" : "rgba(255, 152, 0, 0.3)",
                              color: "#fff",
                              fontWeight: "bold",
                            }}
                          />
                        </TableCell>
                            <TableCell sx={{ color: "#aaa" }}>
                              {dayjs(entry.created_at).format("MM/DD/YYYY HH:mm")}
                            </TableCell>
                            <TableCell sx={{ color: "#aaa" }}>{entry.collection}</TableCell>
                            <TableCell sx={{ color: "#f52a2d" }}>Will be deleted</TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredDuplicates.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              sx={{ color: "#fff" }}
            />
          </TableContainer>
        </>
      )}

      {/* Simulate Dialog */}
      <Dialog open={simulateDialogOpen} onClose={() => setSimulateDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ color: "#fff", background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          ⚠️ Simulation Results - Review Before Deleting
        </DialogTitle>
        <DialogContent sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", color: "#fff", maxHeight: "70vh", overflowY: "auto" }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>⚠️ CRITICAL:</strong> Review the list below carefully. This will delete {stats.duplicateEntries} ledger entries permanently.
              <br />
              <strong>✅ KEEPING:</strong> Migrated entries from production (these are CORRECT).
              <br />
              <strong>🗑️ DELETING:</strong> Entries created in Rowy (duplicates).
            </Typography>
          </Alert>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ background: "rgba(245, 42, 45, 0.1)", border: "1px solid rgba(245, 42, 45, 0.3)" }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>Total Duplicates to Delete</Typography>
                  <Typography variant="h4" sx={{ color: "#f52a2d" }}>{stats.duplicateEntries}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ background: "rgba(33, 150, 243, 0.1)", border: "1px solid rgba(33, 150, 243, 0.3)" }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>Affected Users</Typography>
                  <Typography variant="h4" sx={{ color: "#2196f3" }}>{stats.affectedUsers}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2, borderColor: "rgba(255, 255, 255, 0.1)" }} />
          
          <Typography variant="h6" sx={{ mb: 2, color: "#f52a2d" }}>
            📋 Detailed List of Entries to be DELETED:
          </Typography>
          
          <TableContainer component={Paper} sx={{ backgroundColor: "rgba(0, 0, 0, 0.3)", maxHeight: "400px", overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>User</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>Month/Year</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>Performance %</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>Profit Loss</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>Created</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {duplicates.flatMap((group) =>
                  group.delete.map((entry) => {
                    const userData = userDataMap.get(entry.userId);
                    return (
                      <TableRow key={entry.id} sx={{ backgroundColor: "rgba(245, 42, 45, 0.05)" }}>
                        <TableCell sx={{ color: "#fff" }}>
                          {userData?.name || entry.userId}
                          <br />
                          <Typography variant="caption" sx={{ color: "#aaa" }}>
                            {userData?.email || "Unknown"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {String(entry.month).padStart(2, "0")}/{entry.year}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>{entry.performance.toFixed(4)}%</TableCell>
                        <TableCell sx={{ color: "#fff" }}>${entry.profitLossBalance.toFixed(2)}</TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {dayjs(entry.created_at).format("MM/DD/YYYY HH:mm")}
                        </TableCell>
                        <TableCell sx={{ color: "#aaa", fontSize: "0.75rem", fontFamily: "monospace" }}>
                          {entry.id.substring(0, 20)}...
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Divider sx={{ my: 2, borderColor: "rgba(255, 255, 255, 0.1)" }} />
          
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Individual Users:</strong> {stats.individualDuplicates} duplicates (${stats.totalProfitLossIndividual.toFixed(2)})
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Corporate Users:</strong> {stats.corporateDuplicates} duplicates (${stats.totalProfitLossCorporate.toFixed(2)})
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Total:</strong> {stats.duplicateEntries} duplicates (${stats.totalProfitLoss.toFixed(2)})
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>ℹ️ Note:</strong> User balances are stored in the <code>users</code> collection and will NOT be affected by this deletion.
              Only duplicate performance ledger entries will be removed.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          <Button onClick={() => setSimulateDialogOpen(false)} sx={{ color: "#fff" }}>
            Close
          </Button>
          <Button
            onClick={handleBulkDelete}
            variant="contained"
            color="error"
            sx={{
              background: "linear-gradient(148.97deg, #f52a2d 0%, #c2185b 100%)",
            }}
          >
            Bulk Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: "#fff", background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          Preview Deletion Impact
        </DialogTitle>
        <DialogContent sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", color: "#fff" }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Selected for deletion:</strong> {selectedDuplicates.size} duplicate ledgers
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              • <strong>Balance Impact:</strong> None (balances are stored in users collection)
              <br />
              • <strong>Display Impact:</strong> Performance will show without duplicates
              <br />
              • <strong>Data Safety:</strong> Only duplicate entries will be removed
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          <Button onClick={() => setPreviewDialogOpen(false)} sx={{ color: "#fff" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "#fff", background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", color: "#fff" }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1">
              You are about to delete <strong>{selectedDuplicates.size}</strong> duplicate performance ledgers.
            </Typography>
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • This action cannot be undone
            <br />
            • User balances will NOT be affected
            <br />
            • Performance display will be cleaned up
          </Typography>
        </DialogContent>
        <DialogActions sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: "#fff" }}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={loading}
            sx={{
              background: "linear-gradient(148.97deg, #f52a2d 0%, #c2185b 100%)",
            }}
          >
            {loading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerformanceLedgerDeduplicationTool;
